import { BoxV2 as Box, Flex } from 'portal-commons';
import { FunctionComponent, useRef, useState } from 'react';
import { faFileImage } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import BrandAssetList from './BrandAssetList';
import Emulator from './Emulator';
import { BrandAssetType, BrandAssetExtended } from '../../types';
import { getAttachmentAsImageFile } from '../../apis';
import { toastFlashMessage } from '../../../../../utils';
import AssetActionModal from '../BrandAssets/AssetActionModal';
import { brandAssetController } from '@/apis/core';

interface BrandAssetsEmulatorBlockProps {
  logos: BrandAssetExtended[];
  banners: BrandAssetExtended[];
  isLoading?: boolean;
  onAssetUpdate?: (type: BrandAssetType, index: number, file: File) => void;
  onAssetVerified?: (name: string) => void;
  brandId: string;
}

const BrandAssetsEmulatorBlock: FunctionComponent<
  BrandAssetsEmulatorBlockProps
> = ({
  logos,
  banners,
  isLoading,
  onAssetUpdate,
  brandId,
  onAssetVerified,
}) => {
  const [previewLogo, setPreviewLogo] = useState<string>();
  const [previewBanner, setPreviewBanner] = useState<string>();
  const [loadingLogoIndex, setLoadingLogoIndex] = useState<number>();
  const [loadingBannerIndex, setLoadingBannerIndex] = useState<number>();
  const [selectedAsset, setSelectedAsset] = useState<BrandAssetExtended>();
  const [submitting, setSubmitting] = useState(false);
  const lastClickedLogoIndex = useRef<number>();
  const lastClickedBannerIndex = useRef<number>();

  const resetLoadingIndex = () => {
    setLoadingLogoIndex(undefined);
    setLoadingBannerIndex(undefined);
  };

  const handlePreviewAsset = async (index: number, type: BrandAssetType) => {
    const assets = type === BrandAssetType.Logo ? logos : banners;

    if (type === BrandAssetType.Logo) {
      setLoadingLogoIndex(index);
      lastClickedLogoIndex.current = index;
    } else {
      setLoadingBannerIndex(index);
      lastClickedBannerIndex.current = index;
    }

    let previewFile: File | undefined = assets[index].file;
    if (!previewFile) {
      const file = await getAttachmentAsImageFile(assets[index]);
      if (file) {
        previewFile = file;
        onAssetUpdate?.(type, index, file);
      } else {
        resetLoadingIndex();
        toastFlashMessage('Failed to load asset file', 'error');
        return;
      }
    }

    const previewSetters = {
      [BrandAssetType.Logo]: (imgUrl: string) => {
        if (lastClickedLogoIndex.current === index) {
          setPreviewLogo(imgUrl);
        }
      },
      [BrandAssetType.Banner]: (imgUrl: string) => {
        if (lastClickedBannerIndex.current === index) {
          setPreviewBanner(imgUrl);
        }
      },
    };
    const url = URL.createObjectURL(previewFile);
    previewSetters[type](url);
    resetLoadingIndex();
  };

  const handleVerifyAsset = async () => {
    if (!selectedAsset) return;

    setSubmitting(true);
    const success = await brandAssetController.verifyAsset(
      brandId,
      selectedAsset.name
    );
    setSubmitting(false);
    if (success) {
      onAssetVerified?.(selectedAsset.name);
      setSelectedAsset(undefined);
      toastFlashMessage('Asset verified successfully', 'success');
    }
  };

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        rowGap: '12px',
        padding: '24px 32px',
        border: '1px solid #C0CCD4',
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          alignItems: 'flex-start',
          rowGap: '8px',
          color: '#4D5659',
        }}
      >
        <Flex
          sx={{
            alignItems: 'center',
            columnGap: '8px',
            width: '100%',
            paddingBottom: '12px',
            borderBottom: '1px solid #CCCFD0',
          }}
        >
          <FontAwesomeIcon icon={faFileImage} size="xl" />
          <Box sx={{ fontWeight: 600, fontSize: '18px', lineHeight: '21px' }}>
            Brand Asset Emulator
          </Box>
        </Flex>
        <Box sx={{ fontSize: '14px', lineHeight: '16px' }}>
          Try different logos and banners to see how they will look on an RCS
          Agent.
        </Box>
      </Flex>

      <Flex sx={{ columnGap: '10px', maxHeight: '468px', overflow: 'hidden' }}>
        <Flex
          sx={{
            flex: 1,
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <BrandAssetList
            title="LOGO"
            assets={logos}
            isLoading={isLoading}
            loadingIndex={loadingLogoIndex}
            onSelect={(index) => {
              handlePreviewAsset(index, BrandAssetType.Logo);
            }}
            onVerify={(index) => {
              setSelectedAsset(logos[index]);
            }}
          />
          <BrandAssetList
            title="BANNER"
            assets={banners}
            isLoading={isLoading}
            loadingIndex={loadingBannerIndex}
            onSelect={(index) => {
              handlePreviewAsset(index, BrandAssetType.Banner);
            }}
            onVerify={(index) => {
              setSelectedAsset(banners[index]);
            }}
          />
        </Flex>
        <Emulator logo={previewLogo} banner={previewBanner} />
      </Flex>
      {selectedAsset && (
        <AssetActionModal
          open={!!selectedAsset}
          modalType="verify"
          data={selectedAsset}
          onClose={() => setSelectedAsset(undefined)}
          onSubmit={handleVerifyAsset}
          disabled={submitting}
        />
      )}
    </Flex>
  );
};

export default BrandAssetsEmulatorBlock;
